import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { type CvPassport, type CvPublicProfile } from '../../api/passport';
import PassportViewer from '../../components/Passport/PassportViewer';

// Standalone preview for building/testing the Passport (Phase 1-2).
// /passport-preview?cv=<cvId> renders real data; no params shows the mock so
// the layout can be designed without a running backend.

const MOCK: CvPassport = {
  cv_id: 'mock',
  generated_at: Date.now() / 1000,
  stations: [
    { year: '2011-2015', type: 'education', title: 'Abitur', org: 'Gymnasium Aachen', city: 'Aachen', country: 'Germany' },
    { year: '2015-2019', type: 'education', title: 'B.Sc. Computer Science', org: 'RWTH Aachen', city: 'Aachen', country: 'Germany' },
    { year: '2017', type: 'education', title: 'Exchange Semester', org: 'Washington University', city: 'St. Louis', country: 'USA' },
    { year: '2019-2021', type: 'education', title: 'M.Sc. Computer Science', org: 'RWTH Aachen', city: 'Aachen', country: 'Germany' },
    { year: '2020', type: 'other', title: 'Hackathon Winner', org: 'TechCrunch Disrupt', city: null, country: null },
    { year: '2021-2024', type: 'work', title: 'Software Engineer', org: 'Zalando', city: 'Berlin', country: 'Germany' },
    { year: '2024-present', type: 'work', title: 'Senior Software Engineer', org: 'Uber', city: 'Amsterdam', country: 'Netherlands' },
  ],
  locations: [
    { city: 'Aachen', country: 'Germany', kind: 'study', home: true },
    { city: 'St. Louis', country: 'USA', kind: 'study', home: false },
    { city: 'Berlin', country: 'Germany', kind: 'work', home: true },
    { city: 'Amsterdam', country: 'Netherlands', kind: 'work', home: false },
  ],
  skills: [
    { group: 'Technical', items: ['Python', 'Go', 'React', 'PostgreSQL', 'Kafka'] },
    { group: 'Tools', items: ['Docker', 'Kubernetes', 'Terraform', 'Grafana'] },
    { group: 'Soft', items: ['Mentoring', 'Incident response', 'Technical writing'] },
    { group: 'Domain', items: ['Payments', 'Distributed systems'] },
  ],
  languages: ['German (native)', 'English (fluent)', 'Dutch (basic)'],
  interests: ['Bouldering', 'Analog photography', 'Specialty coffee'],
  highlights: [
    'TechCrunch Disrupt Hackathon winner (2020)',
    'AWS Certified Solutions Architect',
    'Mentored 8 junior engineers across two teams',
  ],
};

const MOCK_REALTALK: CvPublicProfile = {
  cv_id: 'mock',
  real_talk_sentence: 'I build calm, reliable systems and hate flashy hacks that page you at 3am.',
  real_talk_tags: ['backend', 'fintech', 'remote', 'mentoring'],
  is_contactable: true,
};

const PassportPreview: React.FC = () => {
  const [searchParams] = useSearchParams();
  const cvId = searchParams.get('cv');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex flex-col">
      <div className="max-w-2xl w-full mx-auto px-4 pt-5 pb-2 flex items-baseline justify-between">
        <h1 className="text-lg font-bold text-slate-800">
          Passport <span className="text-slate-400 font-normal text-sm">preview</span>
        </h1>
        <span className="text-[11px] text-slate-400">{cvId ? `cv: ${cvId.slice(0, 8)}…` : 'mock data'}</span>
      </div>

      <div className="flex-1 min-h-0">
        {cvId ? (
          <PassportViewer cvId={cvId} onContact={() => alert('Contact flow comes later')} />
        ) : (
          <PassportViewer
            cvId="mock"
            passportOverride={MOCK}
            realTalkOverride={MOCK_REALTALK}
            onContact={() => alert('Contact flow comes later')}
          />
        )}
      </div>
    </div>
  );
};

export default PassportPreview;
